import { parseRequestError } from '@/app/common/utilities'
import { useToast } from '@/shad/ui/toast/use-toast'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { providerPermissionManagementApi } from './api'
import { PROVIDER_PERMISSION_MANAGEMENT_KEYS } from './keys'
import { ProviderUserStatusCode } from './schema'

export type SendProviderUserInviteInput = {
  email: string
  firstName: string
  lastName: string
  roleId: string
}

type SendProviderUserInviteOptions = {
  providerId: string
  programId: number
}

export type ResendProviderUserInviteInput = {
  providerUserId: number
  programId: number
}

type EditProviderUserInfoOptions = {
  providerId: string
}

type EditProviderUserInfoParams = {
  userId: number
  firstName: string
  roleId: string
  lastName: string
  email: string
  status?: number
}

type EditProviderUserRoleOptions = {
  providerId: string
  providerUserId: number
}

type DisableProviderPayload = {
  providerUserId: number
  programId: number
}

function useSendProviderUserInvite({
  providerId,
  programId,
}: SendProviderUserInviteOptions) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (data: SendProviderUserInviteInput) => {
      return providerPermissionManagementApi.sendInvite({
        email: data.email,
        first_name: data.firstName,
        last_name: data.lastName,
        providerId,
        roleId: data.roleId,
        programId,
      })
    },
    onSuccess: () => {
      toast({
        title: 'Invite Sent',
        description: 'Invite has been sent successfully.',
        variant: 'default',
      })
      queryClient.invalidateQueries({
        queryKey:
          PROVIDER_PERMISSION_MANAGEMENT_KEYS.PROVIDER_USERS(providerId),
      })
    },
    onError: (error) => {
      const errorMessage = parseRequestError(error).message
      toast({
        title: 'Invite Failed',
        description: errorMessage,
        variant: 'destructive',
      })
    },
  })
}

function useResendProviderUserInvite() {
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (data: ResendProviderUserInviteInput) => {
      return providerPermissionManagementApi.resendInvite({
        ...data,
      })
    },
    onSuccess: () => {
      toast({
        title: 'Invite Resent',
        description: 'Invite has been resent successfully.',
        variant: 'default',
      })
    },
    onError: (error) => {
      const errorMessage = parseRequestError(error).message
      toast({
        title: 'Invite Resend Failed',
        description: errorMessage,
        variant: 'destructive',
      })
    },
  })
}

function useEditProviderUserInformation({
  providerId,
}: EditProviderUserInfoOptions) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (data: EditProviderUserInfoParams) => {
      return providerPermissionManagementApi.editProviderUserInformation({
        providerId,
        email: data.email,
        first_name: data.firstName,
        roleId: data.roleId,
        last_name: data.lastName,
        providerUserId: data.userId,
        ...(data.status === ProviderUserStatusCode.ACTIVE ||
        data.status === ProviderUserStatusCode.INACTIVE
          ? { status: data.status }
          : {}),
      })
    },
    onSuccess: () => {
      toast({
        title: 'Provider User Updated',
        description: 'Provider user information has been updated successfully.',
      })
      queryClient.invalidateQueries({
        queryKey:
          PROVIDER_PERMISSION_MANAGEMENT_KEYS.PROVIDER_USERS(providerId),
      })
    },
    onError: (error) => {
      const errorMessage = parseRequestError(error).message
      toast({
        title: 'Provider User Update Failed',
        description: errorMessage,
        variant: 'destructive',
      })
    },
  })
}

function useEditProviderUserRole({
  providerId,
  providerUserId,
}: EditProviderUserRoleOptions) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (roleId: number) => {
      return providerPermissionManagementApi.editProviderUserRole({
        roleId,
        providerId,
        providerUserId,
      })
    },
    onSuccess: () => {
      toast({
        title: 'Role Updated',
        description: 'Provider user role has been updated successfully.',
      })
      queryClient.invalidateQueries({
        queryKey:
          PROVIDER_PERMISSION_MANAGEMENT_KEYS.PROVIDER_USERS(providerId),
      })
    },
    onError: (error) => {
      const errorMessage = parseRequestError(error).message
      toast({
        title: 'Role Update Failed',
        description: errorMessage,
        variant: 'destructive',
      })
    },
  })
}

function useDisableProviderUser(providerId: string) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: async (data: DisableProviderPayload) => {
      return providerPermissionManagementApi.disableProviderUser({
        providerId: providerId,
        providerUserId: data.providerUserId,
      })
    },
    onSuccess: () => {
      toast({
        title: 'User Disabled',
        description: 'User has been disabled successfully.',
      })
      queryClient.invalidateQueries({
        queryKey:
          PROVIDER_PERMISSION_MANAGEMENT_KEYS.PROVIDER_USERS(providerId),
      })
    },
    onError: (error) => {
      const errorMessage = parseRequestError(error).message
      toast({
        title: 'User Disable Failed',
        description: errorMessage,
        variant: 'destructive',
      })
    },
  })
}

export {
  useEditProviderUserInformation,
  useEditProviderUserRole,
  useResendProviderUserInvite,
  useSendProviderUserInvite,
  useDisableProviderUser,
}
